import React from 'react';
import { FacebookIcon, InstagramIcon, Mail, MapPin, Phone, Building2, Home, User, ArrowRight, ExternalLink, Castle, Warehouse, Store, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { InstagramLogoIcon } from '@radix-ui/react-icons';

const Footer = () => {
  return (
    <footer className="relative overflow-hidden">
      {/* Background with gradient similar to hero banner */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-900">
        {/* Animated Orbs */}
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl"
            style={{
              width: `${Math.random() * 300 + 100}px`,
              height: `${Math.random() * 300 + 100}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 10 + 15}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
        
        {/* Mesh Grid Overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 pb-32 md:pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Column 1: Logo and Description */}
          <div className="space-y-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-300 to-indigo-200">
                Inkayros
              </h3>
            </motion.div>
            <p className="text-white/80 leading-relaxed">
              Inkayros: Inspira, Innova, Impacta. Encuentra tu próximo hogar con nosotros y transforma tu vida.
            </p>
            <div className="flex space-x-4">
              <motion.a 
                href="https://www.facebook.com/people/Inkayroscom/61578072122722/" 
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <FacebookIcon className="w-5 h-5" />
              </motion.a>
              <motion.a 
                href="https://www.instagram.com/inkayros?igsh=MWFtbGdzN21kYXVmaw=="
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <InstagramIcon className="w-5 h-5" />
              </motion.a>
              <motion.a 
                href="mailto:soporte@inkayros.com" 
                className="p-3 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <Mail className="w-5 h-5" />
              </motion.a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <div className="h-6 w-1 bg-blue-400 rounded-full"></div>
              Enlaces Rápidos
            </h4>
            <ul className="space-y-4">
              {[
                { name: 'Casas', path: '/propiedades/casas' },
                { name: 'Departamentos', path: '/propiedades/departamentos' },
                { name: 'Terrenos', path: '/propiedades/terrenos' },
                { name: 'Locales Comerciales', path: '/propiedades/locales' },
                { name: 'Cocheras', path: '/propiedades/cocheras' },
                { name: 'Oficinas', path: '/propiedades/oficinas' }
              ].map((link, index) => (
                <motion.li key={index} whileHover={{ x: 5 }}>
                  <Link 
                    to={link.path} 
                    className="text-white/70 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{link.name}</span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Column 3: Propiedades y Proyectos (reemplazando la sección de contacto) */}
          <div>
            <h4 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <div className="h-6 w-1 bg-blue-400 rounded-full"></div>
              Buscar Propiedades
            </h4>
            <ul className="space-y-4">
              <motion.li whileHover={{ scale: 1.05, x: 5 }}>
                <Link 
                  to="/buscar?operacion=comprar" 
                  className="flex items-center space-x-3 text-white/70 hover:text-white transition-colors"
                >
                  <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <Home className="w-5 h-5 text-blue-300" />
                  </div>
                  <span>Comprar</span>
                </Link>
              </motion.li>
              <motion.li whileHover={{ scale: 1.05, x: 5 }}>
                <Link 
                  to="/buscar?operacion=alquilar" 
                  className="flex items-center space-x-3 text-white/70 hover:text-white transition-colors"
                >
                  <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-300" />
                  </div>
                  <span>Alquilar</span>
                </Link>
              </motion.li>
              <motion.li whileHover={{ scale: 1.05, x: 5 }}>
                <Link 
                  to="/buscar?operacion=proyectos" 
                  className="flex items-center space-x-3 text-white/70 hover:text-white transition-colors"
                >
                  <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <Castle className="w-5 h-5 text-blue-300" />
                  </div>
                  <span>Proyectos</span>
                </Link>
              </motion.li>
              <motion.li whileHover={{ scale: 1.05, x: 5 }}>
                <Link 
                  to="/map" 
                  className="flex items-center space-x-3 text-white/70 hover:text-white transition-colors"
                >
                  <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-300" />
                  </div>
                  <span>Búsqueda en mapa</span>
                </Link>
              </motion.li>
            </ul>
          </div>

          {/* Column 4: Usuarios InKairos */}
          <div>
            <h4 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <div className="h-6 w-1 bg-blue-400 rounded-full"></div>
              Usuarios Inkayros
            </h4>
            <ul className="space-y-4">
              {[
                { name: 'Vendedores', path: '/usuarios/vendedores', icon: User },
                { name: 'Constructoras', path: '/usuarios/constructoras', icon: Building2 },
                { name: 'Inmobiliarias', path: '/usuarios/inmobiliarias', icon: Home }
              ].map((link, index) => (
                <motion.li key={index} whileHover={{ scale: 1.05, x: 5 }}>
                  <Link 
                    to={link.path} 
                    className="flex items-center space-x-3 text-white/70 hover:text-white transition-colors"
                  >
                    <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                      <link.icon className="w-5 h-5 text-blue-300" />
                    </div>
                    <span>{link.name}</span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider with glow effect */}
        <div className="mt-16 mb-8 relative">
          <div className="h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
          <div className="absolute inset-0 blur-sm bg-blue-400/30"></div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-white/60 text-sm mb-4 md:mb-0">
            © 2025 InKayros. Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-3 md:gap-6">
            <Link 
              to="/privacidad" 
              className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
              whileHover={{ scale: 1.05 }}
            >
              <span>Política de Privacidad</span>
              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
            <Link 
              to="/terminos" 
              className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
              whileHover={{ scale: 1.05 }}
            >
              <span>Términos de Servicio</span>
              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
            <Link
              to="/contacto"  
              className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
              whileHover={{ scale: 1.05 }}
            >
              <span>Contacto</span>
              <Mail className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          </div>
        </div>
      </div>

      {/* CSS for the floating animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(15px); }
        }
      `}</style>
    </footer>
  );
};

export default Footer;